import { useEffect } from 'react';

function injectJsonLd(id: string, schema: Record<string, unknown>) {
  const existing = document.getElementById(id);
  if (existing) {
    existing.textContent = JSON.stringify(schema);
    return;
  }
  const script = document.createElement('script');
  script.type = 'application/ld+json';
  script.id = id;
  script.textContent = JSON.stringify(schema);
  document.head.appendChild(script);
}

function removeJsonLd(id: string) {
  const el = document.getElementById(id);
  if (el) el.remove();
}

const BASE_URL = 'https://aialhku.cn';

export function OrganizationSchema() {
  useEffect(() => {
    injectJsonLd('schema-org', {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: '无码专区',
      url: BASE_URL,
      description: '无码专区是专为内容爱好者打造的精选资源聚合平台，汇聚海量高清无水印素材，分类清晰、更新及时。',
    });
    return () => removeJsonLd('schema-org');
  }, []);
  return null;
}

export function WebSiteSchema() {
  useEffect(() => {
    injectJsonLd('schema-website', {
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      name: '无码专区',
      url: BASE_URL,
      description: '精选资源聚合平台，海量高清无水印素材免费下载',
      potentialAction: {
        '@type': 'SearchAction',
        target: `${BASE_URL}/search?q={search_term_string}`,
        'query-input': 'required name=search_term_string',
      },
    });
    return () => removeJsonLd('schema-website');
  }, []);
  return null;
}

interface BreadcrumbItem {
  name: string;
  item: string;
}

export function BreadcrumbListSchema({ items }: { items: BreadcrumbItem[] }) {
  useEffect(() => {
    injectJsonLd('schema-breadcrumb', {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: items.map((item, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        name: item.name,
        item: item.item,
      })),
    });
    return () => removeJsonLd('schema-breadcrumb');
  }, [items]);
  return null;
}

export function WebPageSchema({
  name,
  description,
  url,
  datePublished,
  dateModified,
}: {
  name: string;
  description: string;
  url: string;
  datePublished?: string;
  dateModified?: string;
}) {
  useEffect(() => {
    const schema: Record<string, unknown> = {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name,
      description,
      url,
    };
    if (datePublished) schema.datePublished = datePublished;
    if (dateModified) schema.dateModified = dateModified;
    injectJsonLd('schema-webpage', schema);
    return () => removeJsonLd('schema-webpage');
  }, [name, description, url, datePublished, dateModified]);
  return null;
}

export function ArticleSchema({
  headline,
  description,
  url,
  image,
  datePublished,
  dateModified,
  authorName = '无码专区',
  category,
}: {
  headline: string;
  description: string;
  url: string;
  image: string;
  datePublished: string;
  dateModified?: string;
  authorName?: string;
  category?: string;
}) {
  useEffect(() => {
    const schema: Record<string, unknown> = {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline,
      description,
      url,
      image,
      datePublished,
      dateModified: dateModified || datePublished,
      author: {
        '@type': 'Organization',
        name: authorName,
      },
      publisher: {
        '@type': 'Organization',
        name: '无码专区',
      },
    };
    if (category) schema.articleSection = category;
    injectJsonLd('schema-article', schema);
    return () => removeJsonLd('schema-article');
  }, [headline, description, url, image, datePublished, dateModified, authorName, category]);
  return null;
}

interface FaqItem {
  question: string;
  answer: string;
}

export function FaqPageSchema({ faqs }: { faqs: FaqItem[] }) {
  useEffect(() => {
    injectJsonLd('schema-faq', {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: faqs.map((faq) => ({
        '@type': 'Question',
        name: faq.question,
        acceptedAnswer: {
          '@type': 'Answer',
          text: faq.answer,
        },
      })),
    });
    return () => removeJsonLd('schema-faq');
  }, [faqs]);
  return null;
}